<!--
  @component DetailsPanel
  @description Right panel displaying item details and edit form with dynamic field support per item type
-->
<script lang="ts">
  import { createEventDispatcher, onDestroy } from 'svelte';
  import { t } from '@/i18n';
  import { selectedItem, selectItem, updateItemInStore } from '@stores/itemStore';
  import { selectedLibraryId, selectedLibraryType } from '@stores/libraryStore';
  import { itemActions } from '@services/itemActionsService';
  import { extractOriginalDate, updateExtraWithOriginalDate } from '@/utils';
  import { createPanelResizer } from '@/utils/panelResize';
  import { ItemDetailsView, ItemDetailsEdit } from '@components/details';
  import type { Item } from '@/types';

  export let width = 350;

  const dispatch = createEventDispatcher<{
    resize: { width: number };
  }>();

  // Panel resize
  const resizer = createPanelResizer({
    minWidth: 250,
    maxWidth: 600,
    direction: 'left',
    onResize: (newWidth) => dispatch('resize', { width: newWidth }),
  });

  // ==========================================================================
  // EDIT STATE
  // ==========================================================================

  let isEditing = false;
  let editData: Partial<Item['data']> | null = null;
  let originalDate = '';

  // Track previous item key to detect actual item changes
  let previousItemKey: string | null = null;

  // Reset edit state only when switching to a different item
  $: {
    const currentKey = $selectedItem?.key ?? null;
    if (currentKey !== previousItemKey) {
      previousItemKey = currentKey;
      isEditing = false;
      editData = null;
      originalDate = '';
    }
  }

  // ==========================================================================
  // EDIT HANDLERS
  // ==========================================================================

  function handleEdit() {
    if (!$selectedItem) return;
    isEditing = true;
    editData = JSON.parse(JSON.stringify($selectedItem.data));
    originalDate = extractOriginalDate($selectedItem.data.extra);
  }

  function handleCancel() {
    isEditing = false;
    editData = null;
    originalDate = '';
  }

  async function handleSave() {
    if (!$selectedItem || !editData || !$selectedLibraryId || !$selectedLibraryType) return;

    // Update extra field with original date
    if (originalDate !== extractOriginalDate($selectedItem.data.extra)) {
      editData.extra = updateExtraWithOriginalDate(editData.extra, originalDate);
    }

    // Clean editData: remove read-only fields that Zotero API doesn't accept in PATCH
    const cleanedData = { ...editData };
    delete cleanedData.key;
    delete cleanedData.version;
    delete cleanedData.dateAdded;
    delete cleanedData.dateModified;
    delete cleanedData.parentItem;
    delete cleanedData.collections;
    delete cleanedData.relations;
    delete cleanedData.tags;

    const result = await itemActions.updateItem(
      $selectedLibraryId,
      $selectedLibraryType,
      $selectedItem.key,
      cleanedData
    );

    if (result.success) {
      // Zotero API returns 204 No Content on PATCH success
      // Construct the updated item locally by merging editData with the existing item
      const updatedItem: Item = {
        ...$selectedItem,
        data: {
          ...$selectedItem.data,
          ...cleanedData,
        },
      };

      updateItemInStore($selectedItem.key, updatedItem);
      selectItem(updatedItem);
      isEditing = false;
      editData = null;
      originalDate = '';
    }
  }

  async function handleDelete() {
    if (!$selectedItem || !$selectedLibraryId || !$selectedLibraryType) return;

    if (confirm($t('item.confirmDeleteMessage'))) {
      await itemActions.deleteItem($selectedLibraryId, $selectedLibraryType, $selectedItem);
      selectItem(null);
    }
  }

  // Cleanup resize listeners on component destroy
  onDestroy(resizer.cleanup);
</script>

<div class="details-panel" style="width: {width}px;">
  <!-- Resize Handle -->
  <div
    class="resize-handle"
    on:mousedown={resizer.start}
    role="presentation"
    aria-hidden="true"
  ></div>

  <!-- Header -->
  <div class="details-header">
    {#if isEditing}
      {$t('item.editItem')}
    {:else if $selectedItem}
      {$t('detailsPanel.title')}
    {:else}
      {$t('detailsPanel.noItemSelected')}
    {/if}
  </div>

  <!-- Content -->
  <div class="details-content">
    {#if $selectedItem && !isEditing}
      <!-- View Mode -->
      <ItemDetailsView item={$selectedItem} on:edit={handleEdit} />
    {:else if isEditing && editData}
      <!-- Edit Mode -->
      <ItemDetailsEdit
        bind:editData
        bind:originalDate
        on:save={handleSave}
        on:cancel={handleCancel}
        on:delete={handleDelete}
      />
    {:else}
      <!-- No Item Selected -->
      <div class="empty-state">
        <div class="empty-icon">📄</div>
        <div class="empty-text">{$t('detailsPanel.selectItemToView')}</div>
      </div>
    {/if}
  </div>
</div>

<style>
  .details-panel {
    background: var(--color-bg-secondary, #383838);
    border-left: 1px solid var(--color-border, #555);
    display: flex;
    flex-direction: column;
    position: relative;
    min-width: 250px;
    max-width: 600px;
  }

  .resize-handle {
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    cursor: ew-resize;
    background: transparent;
    z-index: 10;
    transition: background-color 0.2s;
  }

  .resize-handle:hover {
    background: var(--color-accent, #5c7cfa);
  }

  .details-header {
    padding: 12px 16px;
    background: var(--color-bg-tertiary, #3c3c3c);
    border-bottom: 1px solid var(--color-border, #555);
    font-weight: 600;
    font-size: 13px;
    flex-shrink: 0;
  }

  .details-content {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
  }

  /* Empty state */
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: var(--color-text-muted, #888);
    font-size: 14px;
    gap: 8px;
    padding: 40px;
  }

  .empty-icon {
    font-size: 48px;
    opacity: 0.5;
  }

  .empty-text {
    text-align: center;
    max-width: 200px;
    line-height: 1.4;
  }

  /* ==========================================================================
     Responsive Styles
     ========================================================================== */

  /* Tablet */
  @media (max-width: 1023px) {
    .details-panel {
      min-width: 280px;
      max-width: 400px;
    }
  }

  /* Mobile: Full width */
  @media (max-width: 767px) {
    .details-panel {
      min-width: 0;
      max-width: none;
      width: 100% !important;
      border-left: none;
    }

    .resize-handle {
      display: none;
    }

    .details-header {
      padding: 14px 16px;
      font-size: 14px;
    }

    .details-content {
      padding: 16px;
    }

    .empty-state {
      padding: 24px 16px;
    }
  }
</style>
